import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components/macro';
import { Icon } from 'components/atoms';
import { darken } from 'polished';
import { applyStyleModifiers } from 'styled-components-modifiers';
import { themeType } from 'types';

const BUTTON_MODIFIERS = {
  big: ({ theme }) => `
    font-size: ${theme.sizes.xl};
  `,
  l: ({ theme }) => `
  font-size: ${theme.sizes.l};
`,
  s: ({ theme }) => `
    font-size: ${theme.sizes.s};
  `,
  bold: ({ theme }) => `
    font-weight: ${theme.weight.base};
  `,
  active: ({ theme }) => `
    background-color: ${theme.colors.bgLighter};
    color: ${theme.colors.base};
    &:focus, &:active, &:hover {
      cursor: default;
      color: ${theme.colors.base} !important;
    }
  `,
  disabled: ({ theme }) => `
    color: ${theme.colors.border};
    cursor: not-allowed;
    &:hover {
      cursor: not-allowed;
      color: ${theme.colors.border};
    }
    &:focus {
      outline: 0;
      color: ${theme.colors.border};
    }
    &:active {
      color: ${theme.colors.border};
    }
  `,
  clean: ({ theme }) => `
    background-color: ${theme.components.navActions.backgroundColor};
    color: ${theme.colors.base};
    & div {
      padding-right: ${theme.spacing.xs};
    }

    &:hover {
      background-color: ${theme.components.navActions.backgroundColor};
      cursor: pointer;
      color: ${theme.colors.primary};
    }
    &:focus {
      outline: 0;
      background-color: ${theme.components.navActions.backgroundColor};
      color: ${theme.colors.primary};
    }
    &:active {
      background-color: ${theme.components.navActions.backgroundColor};
      color: ${theme.colors.primary};

    }
  `,
  iconOnly: ({ theme }) => `
    padding-left: ${theme.spacing.xs};
    & div {
      padding-right: 0;
    }
  `,
  clearSpacing: () => `
    padding: 0;
    margin: 0;
  `,
  clearHorizontal: () => `
    padding-right: 0;
    padding-left: 0;
  `,
  clearMargin: () => `
    margin: 0;
  `,
  globalNav: ({ theme }) => `
    min-width: 190px;
    padding: ${theme.spacing.xl};
    padding-top: ${theme.spacing.xs};
    padding-bottom: ${theme.spacing.xs};
  `,
  center: () => `
    justify-content: center;
  `,
  primaryAlt: ({ theme }) => `
    &:hover {
      color: ${theme.colors.primary};
      svg {
        fill: ${theme.colors.primary};
      }
    }
  `,
  clearSpacingTop: () => `
    padding-top: 0;
    margin-top: 0;
  `
};

const ButtonMenu = ({
  children,
  theme,
  onClick,
  className,
  modifiers,
  icon,
  iconAltStyle,
  iconHeight,
  iconForward,
  title,
  label,
}) => {
  const [isHover, setHover] = useState(false);
  const isDisabled = useMemo(() => modifiers.includes('disabled'), [modifiers]);
  const iconColor = useMemo(() => {
    if (modifiers.includes('disabled')) return theme.colors.border;
    if (modifiers.includes('primaryAlt') && isHover) return theme.colors.primary;
    if (!iconAltStyle) return theme.colors.base;
    return theme.colors.base;
  }, [
    isHover,
    iconAltStyle,
    modifiers,
    theme.colors.base,
    theme.colors.primary,
    theme.colors.border,
  ]);

  const hideChildren = useMemo(() => modifiers.includes('iconOnly'), [
    modifiers,
  ]);

  const computedIconHeight = useMemo(() => {
    if (iconHeight) return iconHeight;
    if (iconAltStyle) return 24;
    return 24;
  }, [iconAltStyle, iconHeight]);

  const handleClick = e => (isDisabled ? e.preventDefault() : onClick(e))


  return (
    <Button
      onClick={handleClick}
      className={className}
      modifiers={modifiers}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      iconForward={iconForward}
      title={title}
      {...((label || title) ? { 'aria-label': (label || title) } : {})}
    >
      {icon && !iconForward && (
        <Button.Icon>
          <Icon icon={icon} color={iconColor} height={computedIconHeight} />
        </Button.Icon>
      )}
      {children && !hideChildren && children}
      {icon && iconForward && (
        <Button.Icon>
          <Icon icon={icon} color={iconColor} height={computedIconHeight} />
        </Button.Icon>
      )}
    </Button>
  );
};

const Button = styled.button`
  width: 100%;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  ${props => props.iconForward && 'justify-content: space-between'};
  background: ${props => props.theme.colors.bg};
  color: ${props => props.theme.colors.base};
  padding: ${props => props.theme.spacing.xs};
  border: 0;
  margin-bottom: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.values.borderRadius};
  font-weight: ${props => props.theme.weight.xbase};

  &:hover {
    background-color: ${props => props.theme.colors.bgLighterS};
    cursor: pointer;
  }
  &:focus {
    outline: 0;
    background-color: ${props => darken(0.03, props.theme.colors.bg)};
  }
  &:active {
    background-color: ${props => props.theme.colors.bgLighter};
  }
  ${applyStyleModifiers(BUTTON_MODIFIERS)};
`;

Button.Icon = styled.div`
  display: flex;
  align-items: center;
  padding-right: ${props => props.theme.spacing.xs3};
`;

ButtonMenu.propTypes = {
  children: PropTypes.node,
  theme: themeType.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  icon: PropTypes.string,
  modifiers: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  iconAltStyle: PropTypes.bool,
  iconHeight: PropTypes.number,
  iconForward: PropTypes.bool,
  title: PropTypes.string,
  label: PropTypes.string,
};

ButtonMenu.defaultProps = {
  children: null,
  onClick: () => null,
  className: '',
  modifiers: '',
  icon: null,
  iconAltStyle: false,
  iconHeight: null,
  iconForward: false,
  title: null,
  label: null,
};

export default withTheme(ButtonMenu);
